import React, { useEffect, useState } from 'react';
import { Save, Check, X, ChevronDown, ChevronUp } from 'lucide-react';
import EntityLinksMultiField from '../common/EntityLinksMultiField';
import FormControl from '../common/FormControl';
import Button from '../common/Button';
import Toast, { type ToastMessage } from '../common/Toast';
import {
    Task, TaskCreate, TaskLink, TaskPriority, tasksService,
} from '../../services/tasksService';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea, ValidatedDatePicker } from '../../validation/ValidatedFields';

interface Props {
    sourceTypeId?: number;
    /** Origin by registry type_code (e.g. 'AUDIT_FINDING', 'INCIDENT') — resolved
     *  to source_type_id server-side. Lets any module preset the origin without
     *  knowing the numeric registry id. */
    sourceTypeCode?: string;
    sourceRecordId?: number;
    initial?: Task;
    /** When set, renders a bare <form id={formId}> with no header/footer so the
     *  parent Modal can provide Modal.Header and Modal.Footer with the submit button. */
    formId?: string;
    onSaved: (task: Task) => void;
    onCancel: () => void;
    /** Optional callback so the parent modal can track submission state and
     *  disable its footer buttons / show a "Submitting…" label. */
    onSubmittingChange?: (submitting: boolean) => void;
    /** When true, all form fields are disabled (view-only mode). */
    disabled?: boolean;
}

const PRIORITY_OPTIONS: TaskPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];


const TaskForm: React.FC<Props> = ({
    sourceTypeId, sourceTypeCode, sourceRecordId, initial, formId, onSaved, onCancel, onSubmittingChange, disabled = false,
}) => {
    const { state: authState } = useAuth();
    const currentUserId = (authState.user as any)?.id ?? null;

    const [name, setName] = useState(initial?.name || '');
    const [description, setDescription] = useState(initial?.description || '');
    // Doer defaults to current user on create.
    const [doerId, setDoerId] = useState<number | null>(
        initial?.doer_id ?? currentUserId,
    );
    const [approverId, setApproverId] = useState<number | null>(initial?.approver_id ?? null);
    const [validatorId, setValidatorId] = useState<number | null>(initial?.validator_id ?? null);
    const [dueDate, setDueDate] = useState(initial?.due_date || '');
    const [gracePeriodDays, setGracePeriodDays] = useState<number>(
        initial?.grace_period_days ?? 0,
    );
    const [priority, setPriority] = useState<TaskPriority | ''>(initial?.priority || '');
    const [feedbackRequired, setFeedbackRequired] = useState(initial?.feedback_required || false);
    const [effectivenessRequired, setEffectivenessRequired] = useState(
        initial?.effectiveness_required || false,
    );

    const [links, setLinks] = useState<TaskLink[]>(
        // Prefer server-enriched multi-link list; fall back to legacy single-link
        // fields if a task pre-dates the multi-link migration.
        initial?.linked_entities && initial.linked_entities.length > 0
            ? initial.linked_entities
            : (initial?.linked_type_id && initial?.linked_record_id
                ? [{
                    type_id: initial.linked_type_id,
                    type_code: initial.linked_type_code || undefined,
                    record_id: initial.linked_record_id,
                    label: initial.linked_label || undefined,
                }]
                : []),
    );

    const [users, setUsers] = useState<{ id: number; name: string }[]>([]);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const vh = useFieldValidation();

    // "Advanced" fields (approver, validator, grace period, feedback,
    // effectiveness) are shown by default so reviewers can set an Approver,
    // Third-party Validator, etc. without hunting for them. They remain fully
    // optional — only name + doer are required to save. The disclosure can
    // still be collapsed to keep the form scannable.
    const [showMore, setShowMore] = useState<boolean>(true);

    useEffect(() => {
        vh.reset();
        userService.getUsersDropdown()
            .then(r => setUsers(r || []))
            .catch(() => setUsers([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Once user list lands, if doerId is still null (no auth user available),
    // leave it null and let the user pick — but server will reject create.
    useEffect(() => {
        if (doerId === null && currentUserId) setDoerId(currentUserId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUserId]);

    const userOptions = users.map(u => ({ value: u.id, label: u.name || `#${u.id}` }));
    const priorityOptions = PRIORITY_OPTIONS.map(p => ({ value: p, label: p }));

    // Read-only "Issue" summary — the source + linked-entity references that the
    // task card shows in its footer (e.g. "MEETING 5 — MTG-2026...",
    // "CAPA_ISSUE 1 — [SEED] Data review"). Surfaced here as full, untruncated
    // text so the user can read the whole reference inside the modal.
    const issueLines = (() => {
        const lines: string[] = [];
        if (initial?.source_type_code || initial?.source_label) {
            lines.push([initial.source_type_code, initial.source_label].filter(Boolean).join(' — '));
        }
        const linkList = initial?.linked_entities && initial.linked_entities.length > 0
            ? initial.linked_entities
            : (initial?.linked_type_code || initial?.linked_label
                ? [{ type_code: initial?.linked_type_code, label: initial?.linked_label } as TaskLink]
                : []);
        linkList.forEach((l) => {
            const txt = [l.type_code, l.label].filter(Boolean).join(' — ');
            if (txt) lines.push(txt);
        });
        return lines;
    })();
    const issueText = issueLines.join('\n');

    const handleSave = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!name.trim()) { setToast({ type: 'error', message: 'Name is required' }); return; }
        // Field-format validation (engine) — block submit on any inline error.
        const ok = vh.validateAll([
            { name: 'name', code: 109, value: name, required: true },
            { name: 'description', code: 110, value: description },
            { name: 'dueDate', code: 305, value: dueDate },
        ]);
        if (!ok) return;
        if (!doerId) { setToast({ type: 'error', message: 'Doer is required' }); return; }
        if (gracePeriodDays < 0) { setToast({ type: 'error', message: 'Grace period must be 0 or more days' }); return; }
        if (approverId && validatorId
            && doerId === approverId && approverId === validatorId) {
            setToast({ type: 'error', message: 'Doer, Approver and Third-party Validator cannot all be the same person' });
            return;
        }
        setToast(null); setSaving(true);
        onSubmittingChange?.(true);
        try {
            const payload: TaskCreate = {
                name: name.trim(),
                description: description.trim() || undefined,
                doer_id: doerId,
                approver_id: approverId,
                validator_id: validatorId,
                due_date: dueDate || null,
                grace_period_days: gracePeriodDays,
                priority: priority || null,
                feedback_required: feedbackRequired,
                effectiveness_required: effectivenessRequired,
                linked_entities: links.map(l => ({
                    type_id: l.type_id,
                    record_id: l.record_id,
                    notes: l.notes ?? null,
                })),
                source_type_id: initial?.source_type_id ?? sourceTypeId ?? null,
                source_type_code: sourceTypeCode ?? null,
                source_record_id: initial?.source_record_id ?? sourceRecordId ?? null,
            };
            // `initial` doubles as both "existing task to edit" and "seed
            // values for a new task" (e.g. Create-from-Agenda spawn). A real
            // task always has a positive id; seeds pass id === 0. Treat the
            // latter as a fresh create so we never PUT /tasks/0.
            const isEdit = !!initial && (initial.id ?? 0) > 0;
            const saved = isEdit
                ? await tasksService.update(initial!.id, payload)
                : await tasksService.create(payload);
            onSaved(saved);
        } catch (e: any) {
            setToast({ type: 'error', message: e?.response?.data?.detail || e?.message || 'Failed to save' });
        } finally {
            setSaving(false);
            onSubmittingChange?.(false);
        }
    };

    const fields = (
        <div className="flex flex-col gap-y-4">
            <ValidatedInput
                vh={vh}
                name="name"
                code={109}
                required
                label="Name"
                value={name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                placeholder="Task name"
                disabled={disabled}
                data-testid="INP_task_form_name"
            />

            <ValidatedTextarea
                vh={vh}
                name="description"
                code={110}
                label="Description"
                value={description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                placeholder="Optional description"
                disabled={disabled}
                data-testid="INP_task_form_description"
            />

            {issueText && (
                <FormControl.Textarea
                    label="Issue"
                    value={issueText}
                    readOnly
                    rows={Math.min(Math.max(issueLines.length, 2), 5)}
                    data-testid="TXT_task_form_issue"
                />
            )}

            <FormControl.FormGroup label={`Linked to${links.length > 0 ? ` (${links.length})` : ''}`}>
                <EntityLinksMultiField
                    value={links}
                    onChange={disabled ? () => {} : setLinks}
                    disabled={disabled}
                />
            </FormControl.FormGroup>

            {/* Essentials row: Doer / Due Date / Priority. Approver + Validator
                drop into the "more options" block since most tasks don't use
                them. Doer stays here because it's required. */}
            <div className="grid grid-cols-3 gap-x-6 gap-y-4">
                <FormControl.SearchSelect
                    label="Doer *"
                    options={userOptions}
                    value={doerId}
                    onChange={(v) => setDoerId(v !== null ? Number(v) : null)}
                    placeholder="— select doer —"
                    disabled={disabled}
                    data-testid="SEL_task_form_doer"
                />
                <ValidatedDatePicker
                    vh={vh}
                    name="dueDate"
                    code={305}
                    label="Due Date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    disabled={disabled}
                    data-testid="INP_task_form_due_date"
                />
                <FormControl.SearchSelect
                    label="Priority"
                    options={priorityOptions}
                    value={priority || null}
                    onChange={(v) => setPriority(v ? String(v) as TaskPriority : '')}
                    placeholder="—"
                    clearable
                    disabled={disabled}
                    data-testid="SEL_task_form_priority"
                />
            </div>

            {/* Disclosure toggle — keeps the form light by hiding fields
                most users don't need on a routine task. */}
            <button
                type="button"
                onClick={() => setShowMore(s => !s)}
                disabled={disabled}
                style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 'var(--spacing-xs) 0',
                    color: disabled ? 'var(--text-muted)' : 'var(--text-secondary)',
                    cursor: disabled ? 'default' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-xs)',
                    fontSize: 'var(--font-size-sm)',
                    alignSelf: 'flex-start',
                    opacity: disabled ? 0.6 : 1,
                }}
                aria-expanded={showMore}
                data-testid="BTN_task_form_show_more"
            >
                {showMore ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                {showMore ? 'Hide more options' : 'Show more options'}
            </button>

            {showMore && (
                <div className="flex flex-col gap-y-4">
                    <div className="grid grid-cols-3 gap-x-6 gap-y-4">
                        <FormControl.SearchSelect
                            label="Approver"
                            options={userOptions}
                            value={approverId}
                            onChange={(v) => setApproverId(v !== null ? Number(v) : null)}
                            placeholder="— optional —"
                            clearable
                            disabled={disabled}
                            data-testid="SEL_task_form_approver"
                        />
                        <FormControl.SearchSelect
                            label="Third-party Validator"
                            options={userOptions}
                            value={validatorId}
                            onChange={(v) => setValidatorId(v !== null ? Number(v) : null)}
                            placeholder="— optional —"
                            clearable
                            disabled={disabled}
                            data-testid="SEL_task_form_validator"
                        />
                        <FormControl.Input
                            label="Grace Period (days)"
                            type="number"
                            min={0}
                            step={1}
                            value={String(gracePeriodDays)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setGracePeriodDays(Math.max(0, Number(e.target.value || 0)))}
                            disabled={disabled}
                            data-testid="INP_task_form_grace_period"
                        />
                    </div>

                    <div className="flex gap-4 text-sm">
                        <FormControl.Checkbox
                            label="Reporter feedback expected"
                            checked={feedbackRequired}
                            onChange={(v: boolean) => setFeedbackRequired(v)}
                            disabled={disabled}
                            noMargin
                            data-testid="CHK_task_form_feedback_required"
                        />
                        <FormControl.Checkbox
                            label="Verifier effectiveness check"
                            checked={effectivenessRequired}
                            onChange={(v: boolean) => setEffectivenessRequired(v)}
                            disabled={disabled}
                            noMargin
                            data-testid="CHK_task_form_effectiveness_required"
                        />
                    </div>
                </div>
            )}
        </div>
    );

    if (formId) {
        return (
            <>
                <form id={formId} onSubmit={handleSave} data-testid="MOD_task_form">{fields}</form>
                <Toast toast={toast} onClose={() => setToast(null)} />
            </>
        );
    }

    // Edit mode iff `initial` is a real persisted task (id > 0). A seeded
    // initial (id === 0, e.g. Create-from-Agenda) still renders a fresh
    // "New task" form so the user isn't shown an "Edit" affordance for a
    // record that doesn't exist on the server.
    const isEdit = !!initial && (initial.id ?? 0) > 0;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12,
            padding: 16, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: 14 }}>{isEdit ? 'Edit task' : 'New task'}</strong>
                <button type="button" onClick={onCancel}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
                    data-testid="BTN_task_form_close">
                    <X size={16} />
                </button>
            </div>
            {fields}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button variant="outline" onClick={onCancel} data-testid="BTN_task_form_cancel">Cancel</Button>
                <Button
                    variant={isEdit ? 'update' : 'fill'}
                    icon={isEdit ? Check : Save}
                    onClick={() => handleSave()}
                    disabled={saving}
                    data-testid="BTN_task_form_submit"
                >
                    {saving ? (isEdit ? 'Updating...' : 'Submitting...') : isEdit ? 'Update task' : 'Save task'}
                </Button>
            </div>
            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default TaskForm;
